import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getActivityBank, getActivityTaxonomy } from '../lib/activityBank.js';
import atividadesProntas from '../data/atividadesProntas.js';
import activityGames from '../data/activityGames.js';
import { exportActivityPdf, exportAtividadeProntaPdf } from '../lib/exporters.js';
import { filtrarAtividades, recomendarAtividades } from '../lib/recomendador.js';
import { canEdit, isProjectAdmin } from '../lib/permissions.js';
import { generateId, habilidadeLabels, todayISO } from '../lib/utils.js';
import { consolidatedAssessment } from '../lib/assessmentHistory.js';
import { caseStudyForStudent } from '../features/case-study/case-study.js';
import { activityToLogText } from '../lib/activity-pedagogy.js';
import { ActivityPedagogicalCard } from './ActivityPedagogicalCard.jsx';

const prontasBanco = { atividades: atividadesProntas };

const emptyFilters = { busca: '', eixo: '', habilidade: '', necessidade: '', funcao: '', dificuldade: '' };

function visibleStudents(data, user) {
  if (!user || isProjectAdmin(user)) return [];
  if (user.role === 'school_admin') return (data.students || []).filter((student) => student.schoolId === user.schoolId);
  if (user.role === 'professional') return (data.students || []).filter((student) => student.schoolId === user.schoolId && student.profissionais?.includes(user.uid));
  return (data.students || []).filter((student) => student.schoolId === user.schoolId && student.responsaveis?.includes(user.uid));
}

function splitCsv(value) {
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

function splitLines(value) {
  return String(value || '').split('\n').map((item) => item.trim()).filter(Boolean);
}

function activityUrl(activityId, studentId) {
  return `/atividade/${activityId}${studentId ? `?studentId=${studentId}` : ''}`;
}

function gameUrl(gameId, studentId) {
  return `/jogo/${gameId}${studentId ? `?studentId=${studentId}` : ''}`;
}

function FilterPanel({ filters, setFilters, taxonomy, compact = false }) {
  return <div className='filter-panel'>
    <label>Buscar
      <input value={filters.busca} placeholder='título, objetivo, função...' onChange={(event) => setFilters({ ...filters, busca: event.target.value })} />
    </label>
    <div className='filter-grid'>
      <label>Eixo
        <select value={filters.eixo} onChange={(event) => setFilters({ ...filters, eixo: event.target.value, habilidade: '' })}>
          <option value=''>Todos</option>
          {taxonomy.eixos.map((eixo) => <option value={eixo.id} key={eixo.id}>{eixo.nome}</option>)}
        </select>
      </label>
      <label>Habilidade
        <select value={filters.habilidade} onChange={(event) => setFilters({ ...filters, habilidade: event.target.value })}>
          <option value=''>Todas</option>
          {taxonomy.habilidades.filter((habilidade) => !filters.eixo || habilidade.id.startsWith(filters.eixo.slice(0, 3))).map((habilidade) => <option value={habilidade.id} key={habilidade.id}>{habilidade.nome}</option>)}
        </select>
      </label>
      <label>Necessidade
        <select value={filters.necessidade} onChange={(event) => setFilters({ ...filters, necessidade: event.target.value })}>
          <option value=''>Todas</option>
          {taxonomy.necessidades.map((item) => <option value={item} key={item}>{item}</option>)}
        </select>
      </label>
      <label>Função pedagógica
        <select value={filters.funcao} onChange={(event) => setFilters({ ...filters, funcao: event.target.value })}>
          <option value=''>Todas</option>
          {taxonomy.funcoes.map((item) => <option value={item} key={item}>{item}</option>)}
        </select>
      </label>
      {!compact && <label>Dificuldade
        <select value={filters.dificuldade} onChange={(event) => setFilters({ ...filters, dificuldade: event.target.value })}>
          <option value=''>Todas</option>
          {taxonomy.dificuldades.map((item) => <option value={item} key={item}>Nível {item}</option>)}
        </select>
      </label>}
    </div>
    <div className='actions'><button type='button' onClick={() => setFilters(emptyFilters)}>Limpar filtros</button></div>
  </div>;
}

function ActivityCard({ activity, data, user, student, onUse, onDismiss, recommended = false }) {
  const linkedGame = activityGames.find((game) => game.activityId === activity.id);
  return <article key={activity.id}>
    <h3>{activity.titulo}</h3>
    <small>{activity.eixo} · dificuldade {activity.nivel_dificuldade}{activity.custom ? ' · escola' : ''}{recommended ? ' · prioridade pedagógica' : ''}</small>
    {recommended && activity.motivo_recomendacao && <div className='recommendation-reason'><b>Por que foi indicada:</b> {activity.motivo_recomendacao}</div>}
    <ActivityPedagogicalCard activity={activity} compact />
    <div className='actions'>
      <Link className='action-link' to={activityUrl(activity.id, student?.id)}>Abrir HTML</Link>
      {linkedGame && <Link className='action-link game-link' to={gameUrl(linkedGame.id, student?.id)}>Jogar</Link>}
      <button type='button' onClick={() => exportActivityPdf(activity, { data, student, user })}>Baixar PDF</button>
      {onUse && <button type='button' onClick={() => onUse(activity)}>Usar no atendimento</button>}
      {onDismiss && <button type='button' onClick={() => onDismiss(activity.id)}>Não sugerir agora</button>}
    </div>
  </article>;
}

function GameCard({ game, activity, student }) {
  return <article key={game.id} className='game-card'>
    <h3>{game.titulo}</h3>
    <p>{game.descricao}</p>
    <small>{(game.habilidades_alvo || []).map((id) => habilidadeLabels[id] || id).join(', ')} · atividade base: {activity?.titulo || game.activityId}</small>
    <p className='hint'><b>Métricas:</b> {(game.metricas || []).join(', ')}</p>
    <div className='actions'>
      <Link className='action-link' to={gameUrl(game.id, student?.id)}>Jogar</Link>
    </div>
  </article>;
}

function ProntaCard({ atividade, student, data, onUse }) {
  return <article key={atividade.id} className='pronta-card'>
    <h3>{atividade.titulo}</h3>
    <p>{atividade.objetivo}</p>
    <small>{(atividade.habilidades_alvo || []).map((id) => habilidadeLabels[id] || id).join(', ')}</small>
    <p className='hint'><b>Como aplicar:</b> {atividade.instrucao}</p>
    <div className='actions'>
      <button type='button' onClick={() => exportAtividadeProntaPdf(atividade, { student, data })}>🖨️ Imprimir atividade pronta</button>
      {onUse && <button type='button' onClick={() => onUse(atividade)}>Usar no atendimento</button>}
    </div>
  </article>;
}

export function StudentActivityRecommendations({ student, data, setData, user, onSave }) {
  const assessment = consolidatedAssessment(data.assessments || [], student.id);
  const bank = useMemo(() => getActivityBank(data), [data]);
  const taxonomy = useMemo(() => getActivityTaxonomy(bank), [bank]);
  const [filters, setFilters] = useState(emptyFilters);
  const [dismissedIds, setDismissedIds] = useState([]);
  const studentLogs = useMemo(() => (data.logs || []).filter((log) => log.studentId === student.id || log.alunoId === student.id), [data.logs, student.id]);
  const studentGoals = useMemo(() => (data.peis || [])
    .filter((pei) => pei.studentId === student.id || pei.alunoId === student.id)
    .flatMap((pei) => pei.metas || []), [data.peis, student.id]);
  const caseStudy = useMemo(() => caseStudyForStudent(data.caseStudies, student.id), [data.caseStudies, student.id]);
  const hasPedagogicalContext = Boolean(assessment || studentGoals.length || caseStudy.blocks?.barriers?.summary);
  const recommendationContext = useMemo(() => ({ logs: studentLogs, metasPEI: studentGoals, caseStudy }), [studentLogs, studentGoals, caseStudy]);
  const recommendations = useMemo(() => recomendarAtividades(assessment || {}, student, bank, {
    topN: 12,
    filtros: filters,
    ...recommendationContext,
  }), [assessment, student, bank, filters, recommendationContext]);
  // Sem estudo de caso, meta ou avaliação suficientes, mostramos o catálogo geral. O
  // diagnóstico isolado nunca inicia uma recomendação e diferenças de vocabulário não
  // podem esvaziar o catálogo.
  const fallback = useMemo(() => filtrarAtividades(bank, filters).slice(0, 16), [bank, filters]);
  const list = hasPedagogicalContext
    ? recommendations.filter((activity) => !dismissedIds.includes(activity.id)).slice(0, 6)
    : fallback;

  const prontasRecomendadas = useMemo(() => recomendarAtividades(assessment || {}, student, prontasBanco, {
    topN: 8,
    ...recommendationContext,
  }), [assessment, student, recommendationContext]);
  const prontasFallback = useMemo(() => filtrarAtividades(prontasBanco, {}).slice(0, 4), []);
  const prontas = hasPedagogicalContext
    ? prontasRecomendadas.filter((activity) => !dismissedIds.includes(activity.id)).slice(0, 4)
    : prontasFallback;
  const gameSuggestions = useMemo(() => {
    const rankedIds = new Set((list || []).map((activity) => activity.id));
    const mapped = activityGames.map((game) => ({ game, activity: bank.atividades.find((activity) => activity.id === game.activityId) })).filter((item) => item.activity);
    const recommended = mapped.filter((item) => rankedIds.has(item.activity.id));
    return (recommended.length ? recommended : mapped).slice(0, 3);
  }, [bank, list]);

  function registrar(activity) {
    const log = {
      id: generateId('log'),
      studentId: student.id,
      data: todayISO(),
      autorUid: user.uid,
      atividadeId: activity.id,
      objetivo: activity.objetivo,
      estrategia: activity.pronta ? `${activity.titulo}\nComo aplicar: ${activity.instrucao}` : activityToLogText(activity),
      observacao: hasPedagogicalContext ? 'Atividade escolhida pelo professor entre sugestões pedagógicas explicadas.' : 'Atividade selecionada pelo professor no catálogo geral.'
    };
    const next = { ...data, logs: [log, ...(data.logs || [])] };
    setData(next);
    onSave?.(next);
  }

  return <>
    <CardShell>
      <h2>Jogos interativos</h2>
      <p className='hint'>MVP de jogos curtos no navegador, com métricas simples para registrar no atendimento e alimentar a evolução do aluno.</p>
      {gameSuggestions.length === 0 && <p className='hint'>Nenhum jogo interativo encontrado para os critérios atuais.</p>}
      <div className='list game-grid'>{gameSuggestions.map(({ game, activity }) => <GameCard key={game.id} game={game} activity={activity} student={student} />)}</div>
    </CardShell>
    <CardShell>
      <h2>Atividades prontas para imprimir</h2>
      <p className='hint'>Fichas de uma página só, sem preparo de material: é imprimir e aplicar. Indicadas pelas mesmas prioridades pedagógicas do aluno.</p>
      {prontas.length === 0 && <p className='hint'>Nenhuma ficha pronta encontrada para os critérios atuais.</p>}
      <div className='list prontas-grid'>{prontas.map((atividade) => <ProntaCard key={atividade.id} atividade={atividade} student={student} data={data} onUse={registrar} />)}</div>
    </CardShell>
    <CardShell>
      <h2>Atividades detalhadas</h2>
      <p className='hint'>Roteiro completo com variações, adaptações e critério de domínio — para quando o professor quiser planejar além da ficha pronta.</p>
      {!hasPedagogicalContext && <div className='privacy-box'><h3>Sugestões ainda não personalizadas</h3><p>O diagnóstico isolado não gera sugestão. Registre uma meta, uma barreira ou uma avaliação pedagógica para receber indicações explicadas.</p></div>}
      <FilterPanel filters={filters} setFilters={setFilters} taxonomy={taxonomy} compact />
      <p className='hint'>{list.length} atividade(s) encontrada(s) para os critérios atuais.</p>
      <div className='list'>{list.map((activity) => <ActivityCard key={activity.id} activity={activity} data={data} user={user} student={student} onUse={registrar} onDismiss={hasPedagogicalContext ? (id) => setDismissedIds((current) => [...new Set([...current, id])]) : null} recommended={hasPedagogicalContext} />)}</div>
    </CardShell>
  </>;
}

export function ActivityCatalog({ user, data, setData, onSave }) {
  const bank = useMemo(() => getActivityBank(data), [data]);
  const taxonomy = useMemo(() => getActivityTaxonomy(bank), [bank]);
  const students = useMemo(() => visibleStudents(data, user), [data, user]);
  const [studentId, setStudentId] = useState(students[0]?.id || '');
  const selectedStudent = students.find((student) => student.id === studentId) || students[0];
  const [filters, setFilters] = useState(emptyFilters);
  const filtered = useMemo(() => filtrarAtividades(bank, filters), [bank, filters]);
  const [form, setForm] = useState({ titulo: '', objetivo: '', eixo: 'cognitivo', habilidade: 'cog_atencao', funcoes: '', necessidades: 'TEA', min: 4, max: 14, dificuldade: 2, materiais: '', passos: '', progresso: '', adaptacao: '', fonte: 'Curadoria local da escola' });

  function add(event) {
    event.preventDefault();
    const atividade = {
      id: generateId('custom_atv'),
      custom: true,
      titulo: form.titulo,
      eixo: form.eixo,
      habilidades_alvo: [form.habilidade],
      funcoes: splitCsv(form.funcoes),
      objetivo: form.objetivo,
      necessidades_indicadas: splitCsv(form.necessidades),
      faixa_etaria: { min: Number(form.min), max: Number(form.max) },
      nivel_dificuldade: Number(form.dificuldade),
      materiais: splitCsv(form.materiais),
      passo_a_passo: splitLines(form.passos),
      como_medir_progresso: form.progresso,
      adaptacoes: { orientacao: form.adaptacao },
      fonte: form.fonte
    };
    const next = { ...data, customActivities: [atividade, ...(data.customActivities || [])] };
    setData(next);
    onSave?.(next);
    setForm({ ...form, titulo: '', objetivo: '', funcoes: '', materiais: '', passos: '', progresso: '', adaptacao: '' });
  }

  return <>
    <header className='header'><div><p>curadoria</p><h1>Catálogo pedagógico</h1><span>{bank.atividades.length} atividades disponíveis. As atividades com versão interativa exibem o botão Jogar no próprio card.</span></div></header>
    <div className='split'>
      {canEdit(user) && <CardShell><h2>Nova atividade da escola</h2><form className='form' onSubmit={add}>
        <label>Título<input required value={form.titulo} onChange={(event)=>setForm({...form,titulo:event.target.value})}/></label>
        <label>Objetivo<input required value={form.objetivo} onChange={(event)=>setForm({...form,objetivo:event.target.value})}/></label>
        <label>Eixo<select value={form.eixo} onChange={(event)=>setForm({...form,eixo:event.target.value})}>{taxonomy.eixos.map((eixo)=><option key={eixo.id} value={eixo.id}>{eixo.nome}</option>)}</select></label>
        <label>Habilidade-alvo<select value={form.habilidade} onChange={(event)=>setForm({...form,habilidade:event.target.value})}>{taxonomy.habilidades.map((habilidade)=><option key={habilidade.id} value={habilidade.id}>{habilidade.nome}</option>)}</select></label>
        <label>Funções pedagógicas separadas por vírgula<input value={form.funcoes} placeholder='atenção, autonomia, comunicação funcional' onChange={(event)=>setForm({...form,funcoes:event.target.value})}/></label>
        <label>Necessidades indicadas<input value={form.necessidades} onChange={(event)=>setForm({...form,necessidades:event.target.value})}/></label>
        <div className='inline-grid'><label>Idade mínima<input type='number' value={form.min} onChange={(event)=>setForm({...form,min:event.target.value})}/></label><label>Idade máxima<input type='number' value={form.max} onChange={(event)=>setForm({...form,max:event.target.value})}/></label><label>Dificuldade<input type='number' min='1' max='3' value={form.dificuldade} onChange={(event)=>setForm({...form,dificuldade:event.target.value})}/></label></div>
        <label>Materiais separados por vírgula<input required value={form.materiais} onChange={(event)=>setForm({...form,materiais:event.target.value})}/></label>
        <label>Passo a passo, um por linha<textarea required value={form.passos} onChange={(event)=>setForm({...form,passos:event.target.value})}/></label>
        <label>Como medir progresso<input required value={form.progresso} onChange={(event)=>setForm({...form,progresso:event.target.value})}/></label>
        <label>Adaptação<textarea value={form.adaptacao} onChange={(event)=>setForm({...form,adaptacao:event.target.value})}/></label>
        <label>Fonte<input required value={form.fonte} onChange={(event)=>setForm({...form,fonte:event.target.value})}/></label>
        <button>Adicionar ao catálogo local</button>
      </form></CardShell>}
      <CardShell><h2>Atividades disponíveis</h2>
        {students.length > 0 && <label>Aluno para personalizar HTML/PDF<select value={studentId} onChange={(event) => setStudentId(event.target.value)}>{students.map((student) => <option key={student.id} value={student.id}>{student.nome} — {student.serie}</option>)}</select></label>}
        <FilterPanel filters={filters} setFilters={setFilters} taxonomy={taxonomy} />
        <p className='hint'>{filtered.length} atividade(s) encontrada(s).</p>
        <div className='list'>{filtered.slice(0, 120).map((activity) => <ActivityCard key={activity.id} activity={activity} data={data} user={user} student={selectedStudent} />)}</div>
      </CardShell>
    </div>
  </>;
}

function CardShell({ children }) {
  return <section className='card'>{children}</section>;
}
